import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Pencil } from 'react-bootstrap-icons';

import { MFA_FACTOR, SMS_MFA_FEATURE_FLAG } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSMenu, SDSMenuItem } from '@repo/sds/SDSMenu';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSRadio } from '@repo/sds/SDSRadio';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';
import { useFeatureFlag } from '@repo/ui/hooks/use-feature-flag';

import authenticatorAppIntro from './assets/authenticator-app-intro.svg';
import { figmaReplacementChoices, formatMfaFactorDate } from './mfa-authenticator-manage-utils';
import { MfaHero } from './mfa-hero';
import { resolvePrimaryFactor } from './mfa-method-picker-utils';
import { MfaSettingsPage } from './mfa-settings-page';

const MFA = DataTestIds.COMMON.SECURITY.MFA;
const TOTP_APP_NAME_MAX_LENGTH = 64;

export type NewPrimaryMethod = 'totp' | 'sms' | 'email' | 'passkey';

function remainingPrimaryChoices(enrolled: readonly string[]): NewPrimaryMethod[] {
  const choices: NewPrimaryMethod[] = [];
  if (enrolled.includes(MFA_FACTOR.Sms)) {
    choices.push('sms');
  }
  if (enrolled.includes(MFA_FACTOR.Email)) {
    choices.push('email');
  }
  if (enrolled.includes(MFA_FACTOR.Passkey)) {
    choices.push('passkey');
  }
  return choices;
}

export function MfaSelect({
  onChange,
  options,
  placeholder,
  value,
}: {
  onChange: (value: NewPrimaryMethod) => void;
  options: ReadonlyArray<{ disabled: boolean; label: string; value: NewPrimaryMethod }>;
  placeholder: string;
  value?: NewPrimaryMethod;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find(option => option.value === value);

  return (
    <SDSMenu
      align="start"
      contentClassName="MfaSelect__menu"
      dataTestId={`${MFA.NEW_PRIMARY}-menu`}
      modal
      onOpenChange={setOpen}
      open={open}
      side="bottom"
      trigger={
        <span className="MfaSelect__field">
          <SDSText
            className="MfaSelect__value"
            color={selected ? 'text-secondary' : 'muted'}
            text={selected?.label ?? placeholder}
            type="body-medium"
          />
          <SDSIcon color="text-secondary" icon={open ? <ChevronUp /> : <ChevronDown />} size="small" />
        </span>
      }
      triggerClassName="MfaSelect__trigger"
      triggerProps={{ 'data-testid': MFA.NEW_PRIMARY }}
    >
      {options.map(option => (
        <SDSMenuItem
          dataTestId={`${MFA.NEW_PRIMARY}-${option.value}`}
          disabled={option.disabled}
          key={option.value}
          onClick={() => {
            if (option.disabled) {
              return;
            }
            onChange(option.value);
          }}
        >
          {option.label}
        </SDSMenuItem>
      ))}
    </SDSMenu>
  );
}

export function MfaManageDetailRow({
  action,
  badge,
  dataTestId,
  label,
  value,
}: {
  action?: ReactNode;
  badge?: ReactNode;
  dataTestId?: string;
  label: string;
  value: string;
}) {
  return (
    <div className="MfaAuthenticatorManage__row">
      <SDSText className="MfaAuthenticatorManage__label" color="muted" text={label} type="body-small" />
      <span className="MfaAuthenticatorManage__value">
        {badge ?? <SDSText dataTestId={dataTestId} text={value} type="body-medium" />}
        {action}
      </span>
    </div>
  );
}

export function ManageActionRow({
  danger,
  dangerDescription,
  description,
  onClick,
  testId,
  title,
}: {
  danger?: boolean;
  dangerDescription?: boolean;
  description?: string;
  onClick: () => void;
  testId: string;
  title: string;
}) {
  return (
    <div className="MfaAuthenticatorManage__action">
      <SDSPrimitiveButton ariaLabel={title} className="MfaMethodChoice__hit" dataTestId={testId} onClick={onClick}>
        <span className="MfaMethodChoice__copy">
          <SDSText color={danger ? 'alert' : undefined} text={title} type="body-medium" />
          {description ? (
            <SDSText color={dangerDescription ? 'alert' : 'muted'} text={description} type="body-small" />
          ) : null}
        </span>
        <span aria-hidden="true" className="MfaMethodChoice__chevron">
          <SDSIcon color={danger ? 'alert' : 'text-secondary'} icon={<ChevronRight />} size="small" />
        </span>
      </SDSPrimitiveButton>
    </div>
  );
}

export function MfaManageLayout({
  actions,
  children,
  className = 'MfaTotpDialog MfaAuthenticatorManageDialog',
  closeTestId,
  dataTestId,
  hero,
  isMobile,
  onClose,
  t,
  title,
}: {
  actions: ReactNode;
  children: ReactNode;
  className?: string;
  closeTestId: string;
  dataTestId: string;
  hero: ReactNode;
  isMobile: boolean;
  onClose: () => void;
  t: TFunction<'common'>;
  title: string;
}) {
  const content = (
    <SDSFlexbox direction="column" fullWidth gap="text-x-large">
      {hero}
      <SDSFlexbox direction="column" fullWidth gap="text-large">
        <SDSText text={title} type={isMobile ? 'h5' : 'h4'} />
        {children}
      </SDSFlexbox>
      <SDSFlexbox direction="column" fullWidth gap="text-x-large">
        {actions}
        {isMobile ? null : (
          <SDSButton
            dataTestId={closeTestId}
            isFullWidth
            mode="outline"
            onClick={onClose}
            text={t('security.mfa.totpClose')}
          />
        )}
      </SDSFlexbox>
    </SDSFlexbox>
  );

  if (isMobile) {
    return (
      <MfaSettingsPage
        ariaLabel={title}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={dataTestId}
        onBack={onClose}
      >
        {content}
      </MfaSettingsPage>
    );
  }

  return (
    <SDSDialog
      className={className}
      dataTestId={dataTestId}
      isOpen
      onClose={onClose}
      showCloseButton={false}
      size="small"
    >
      {content}
    </SDSDialog>
  );
}

export function PrimaryChoiceRow({
  checked,
  description,
  icon,
  name,
  onSelect,
  testId,
  title,
  value,
}: {
  checked: boolean;
  description: string;
  icon: ReactNode;
  name: string;
  onSelect: (value: NewPrimaryMethod) => void;
  testId: string;
  title: string;
  value: NewPrimaryMethod;
}) {
  return (
    <div className="MfaMethodChoice__option MfaMethodChoice__option--radio">
      <SDSRadio
        ariaLabel={title}
        checked={checked}
        dataTestId={`${testId}-radio`}
        name={name}
        onChange={isChecked => {
          if (isChecked) {
            onSelect(value);
          }
        }}
        value={value}
      />
      <SDSPrimitiveButton
        ariaLabel={title}
        className="MfaMethodChoice__hit"
        dataTestId={testId}
        onClick={() => onSelect(value)}
      >
        <span aria-hidden="true" className="MfaMethodChoice__icon MfaMethodChoice__icon--badge">
          {icon}
        </span>
        <span className="MfaMethodChoice__copy">
          <SDSText text={title} type="body-medium" />
          <SDSText color="muted" text={description} type="body-small" />
        </span>
      </SDSPrimitiveButton>
    </div>
  );
}

export function MfaPrimaryChoiceList({
  name,
  onSelect,
  options,
  selected,
}: {
  name: string;
  onSelect: (value: NewPrimaryMethod) => void;
  options: ReadonlyArray<{
    description: string;
    icon: ReactNode;
    title: string;
    value: NewPrimaryMethod;
  }>;
  selected?: NewPrimaryMethod;
}) {
  return (
    <SDSFlexbox
      className="MfaMethodChoice MfaMethodChoice--desktop"
      dataTestId={MFA.NEW_PRIMARY}
      direction="column"
      fullWidth
      gap="text-medium"
    >
      {options.map(option => (
        <PrimaryChoiceRow
          checked={selected === option.value}
          description={option.description}
          icon={option.icon}
          key={option.value}
          name={name}
          onSelect={onSelect}
          testId={`${MFA.NEW_PRIMARY}-${option.value}`}
          title={option.title}
          value={option.value}
        />
      ))}
    </SDSFlexbox>
  );
}

export function MfaRemoveConfirmDialog({
  backLabel,
  cancelLabel,
  children,
  confirmDisabled,
  confirmLabel,
  dataTestId,
  dialogClassName,
  hero,
  isMobile,
  lead,
  onBack,
  onClose,
  onConfirm,
  title,
}: {
  backLabel: string;
  cancelLabel: string;
  children?: ReactNode;
  confirmDisabled?: boolean;
  confirmLabel: string;
  dataTestId: string;
  dialogClassName: string;
  hero: ReactNode;
  isMobile: boolean;
  lead: string;
  onBack: () => void;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
}) {
  if (isMobile) {
    return (
      <MfaSettingsPage ariaLabel={title} backLabel={backLabel} dataTestId={dataTestId} onBack={onClose}>
        <SDSText text={title} type="h5" />
        {hero}
        <SDSText text={lead} type="body-medium" />
        {children}
        <SDSFlexbox direction="column" fullWidth gap="button-medium">
          <SDSButton
            dataTestId={MFA.CONFIRM}
            isDisabled={confirmDisabled}
            isFullWidth
            onClick={onConfirm}
            status="alert"
            text={confirmLabel}
          />
          <SDSButton dataTestId={MFA.CANCEL} isFullWidth mode="outline" onClick={onBack} text={cancelLabel} />
        </SDSFlexbox>
      </MfaSettingsPage>
    );
  }

  return (
    <SDSDialog
      className={dialogClassName}
      dataTestId={dataTestId}
      isOpen
      onClose={onClose}
      primaryAction={{
        dataTestId: MFA.CONFIRM,
        disabled: confirmDisabled,
        label: confirmLabel,
        onClick: onConfirm,
        status: 'alert',
      }}
      secondaryAction={{ dataTestId: MFA.CANCEL, label: cancelLabel, onClick: onBack }}
      showCloseButton={false}
      size="small"
    >
      <SDSFlexbox direction="column" fullWidth gap="text-x-large">
        {hero}
        <SDSFlexbox direction="column" fullWidth gap="text-large">
          <SDSText text={title} type="h4" />
          <SDSText color="text-secondary" text={lead} type="body-extra-large" />
        </SDSFlexbox>
        {children}
      </SDSFlexbox>
    </SDSDialog>
  );
}

export function MfaAuthenticatorManageDialog({
  accountPrimary,
  enrolled,
  enrolledAt,
  isMobile,
  lastUsedAt,
  onClose,
  onRemove,
  onReplace,
  onSaveAppName,
  t,
  totpAppName,
}: {
  accountPrimary?: string;
  enrolled: readonly string[];
  enrolledAt?: string | null;
  isMobile: boolean;
  lastUsedAt?: string | null;
  onClose: () => void;
  onRemove: () => void;
  onReplace: () => void;
  onSaveAppName: (name: string) => void;
  t: TFunction<'common'>;
  totpAppName?: string | null;
}) {
  const defaultName = t('security.mfa.totpEnrolledHint');
  const [appName, setAppName] = useState(() =>
    totpAppName && totpAppName.trim().length > 0 ? totpAppName.trim() : defaultName,
  );
  const [editingName, setEditingName] = useState(false);
  const addedOn = formatMfaFactorDate(enrolledAt);
  const lastUsedOn = formatMfaFactorDate(lastUsedAt);
  const isPrimary = resolvePrimaryFactor(enrolled, accountPrimary) === MFA_FACTOR.Totp;
  const title = t('security.mfa.manageTotpTitle');

  const saveAppName = (next: string) => {
    const trimmed = next.trim().slice(0, TOTP_APP_NAME_MAX_LENGTH);
    setAppName(trimmed.length > 0 ? trimmed : defaultName);
    setEditingName(false);
    onSaveAppName(trimmed);
  };

  const details = (
    <SDSFlexbox
      className={isMobile ? 'MfaAuthenticatorManage MfaAuthenticatorManage--mobile' : 'MfaAuthenticatorManage'}
      direction="column"
      fullWidth
    >
      {editingName ? (
        <div className="MfaAuthenticatorManage__row">
          <SDSText
            className="MfaAuthenticatorManage__label"
            color="muted"
            text={t('security.mfa.appName')}
            type="body-small"
          />
          <SDSTextInput
            ariaLabel={t('security.mfa.appName')}
            dataTestId={MFA.TOTP_APP_NAME}
            isFullWidth
            maxLength={TOTP_APP_NAME_MAX_LENGTH}
            onBlur={() => saveAppName(appName)}
            onChange={setAppName}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                saveAppName(appName);
              }
            }}
            placeholder={t('security.mfa.appName')}
            value={appName}
          />
        </div>
      ) : (
        <MfaManageDetailRow
          action={
            <SDSPrimitiveButton
              ariaLabel={t('security.mfa.renameApp')}
              className="MfaAuthenticatorManage__rename"
              dataTestId={MFA.TOTP_APP_NAME_EDIT}
              onClick={() => setEditingName(true)}
            >
              <SDSIcon color="text-secondary" icon={<Pencil />} size="small" />
            </SDSPrimitiveButton>
          }
          label={t('security.mfa.appName')}
          value={appName}
        />
      )}
      <MfaManageDetailRow
        badge={
          isPrimary ? (
            <span className="MfaSettingsSection__pill MfaSettingsSection__pill--on">
              <SDSText color="primary" text={t('security.mfa.rolePrimary')} type="body-small" />
            </span>
          ) : undefined
        }
        label={t('security.mfa.methodStatus')}
        value={t(isPrimary ? 'security.mfa.yes' : 'security.mfa.no')}
      />
      {addedOn ? (
        <MfaManageDetailRow dataTestId={MFA.TOTP_ADDED} label={t('security.mfa.added')} value={addedOn} />
      ) : null}
      {lastUsedOn ? (
        <MfaManageDetailRow dataTestId={MFA.TOTP_LAST_USED} label={t('security.mfa.lastUsed')} value={lastUsedOn} />
      ) : null}
    </SDSFlexbox>
  );

  const actions = (
    <div className="MfaAuthenticatorManage__actions">
      <ManageActionRow
        description={t('security.mfa.changeTotpDescription')}
        onClick={onReplace}
        testId={MFA.TOTP_REPLACE}
        title={t('security.mfa.changeTotp')}
      />
      <ManageActionRow
        danger
        dangerDescription
        description={isPrimary ? t('security.mfa.removeTotpChoosePrimaryFirst') : undefined}
        onClick={onRemove}
        testId={MFA.TOTP_REMOVE}
        title={t('security.mfa.removeTotp')}
      />
    </div>
  );

  return (
    <MfaManageLayout
      actions={actions}
      closeTestId={MFA.TOTP_CLOSE}
      dataTestId={MFA.TOTP_MANAGE}
      hero={<MfaHero plain={isMobile} setup src={authenticatorAppIntro} />}
      isMobile={isMobile}
      onClose={onClose}
      t={t}
      title={title}
    >
      {details}
    </MfaManageLayout>
  );
}

export function MfaAuthenticatorRemoveDialog({
  accountPrimary,
  email,
  enrolled,
  isMobile,
  onBack,
  onClose,
  onConfirm,
  smsPhoneMasked,
  t,
}: {
  accountPrimary?: string;
  email?: string;
  enrolled: readonly string[];
  isMobile: boolean;
  onBack: () => void;
  onClose: () => void;
  onConfirm: (replacement?: NewPrimaryMethod) => void;
  smsPhoneMasked?: string | null;
  t: TFunction<'common'>;
}) {
  const smsSetupAvailable = useFeatureFlag(SMS_MFA_FEATURE_FLAG);
  const remaining = remainingPrimaryChoices(enrolled);
  const isPrimary = resolvePrimaryFactor(enrolled, accountPrimary) === MFA_FACTOR.Totp;
  const [selected, setSelected] = useState<NewPrimaryMethod | undefined>();
  const needsPrimary = isPrimary;
  const canRemove = needsPrimary ? Boolean(selected) : true;
  const confirmRemove = () => {
    if (needsPrimary) {
      if (!selected) {
        return;
      }
      onConfirm(selected);
      return;
    }
    onConfirm();
  };
  const title = t('security.mfa.removeTotpTitle');
  const primaryChoices = figmaReplacementChoices('totp', t, {
    email: remaining.includes('email') ? email : undefined,
    passkeyCount: enrolled.includes(MFA_FACTOR.Passkey) ? 1 : 0,
    smsOffered: enrolled.includes(MFA_FACTOR.Sms) || smsSetupAvailable,
    smsPhoneMasked: enrolled.includes(MFA_FACTOR.Sms) ? (smsPhoneMasked ?? undefined) : undefined,
  });
  const primaryField = needsPrimary ? (
    <MfaPrimaryChoiceList
      name="mfa-totp-new-primary"
      onSelect={setSelected}
      options={primaryChoices}
      selected={selected}
    />
  ) : null;

  return (
    <MfaRemoveConfirmDialog
      backLabel={t('security.mfa.backToSecurity')}
      cancelLabel={t('security.mfa.cancel')}
      confirmDisabled={!canRemove}
      confirmLabel={needsPrimary ? t('security.mfa.setPrimaryAndRemove') : t('security.mfa.removeTotp')}
      dataTestId={MFA.TOTP_REMOVE_CONFIRM}
      dialogClassName="MfaTotpDialog MfaRemoveConfirmDialog"
      hero={<MfaHero plain={isMobile} setup src={authenticatorAppIntro} />}
      isMobile={isMobile}
      lead={needsPrimary ? t('security.mfa.removeTotpChoosePrimaryLead') : t('security.mfa.removeTotpDescription')}
      onBack={onBack}
      onClose={onClose}
      onConfirm={confirmRemove}
      title={title}
    >
      {primaryField}
    </MfaRemoveConfirmDialog>
  );
}
